import { labelId, labelRegion, labelArea, labelCentro } from '../../../../../features/labels/labels';
import { FEEDING_RATES, rateAlarmEntries } from '../../../utils/feedingRates';
import { parseScappLocalDate } from '../../../utils/parseScappDate';

const METRICS = [
  ['delivered_food', 'delivered_food', 'Alimento entregado', 'sum'],
  ['sfr_value', 'average_sfr', 'SFR promedio', 'average'],
  ['alarms_count', 'alarms', 'Alarmas', 'sum'],
  ['perdidos_count', 'ppa', 'PPA', 'sum'],
  ['visitas_count', 'visits', 'Visitas', 'sum'],
  ['alarms_by_ton', 'alarms_per_ton', 'Alarmas por tonelada', 'sum'],
  ['alarms_by_visits', 'alarms_per_visit', 'Alarmas por visita', 'sum'],
  ['fasts_count', 'fasts', 'Ayunos', 'sum'],
];

export const DEFAULT_ANALYTICS_CONTENT = Object.freeze({
  summary: true,
  periodDetail: true,
  alarmRate: true,
  ranking: true,
  alarmEvents: true,
  notes: true,
  dailyDetail: true,
});

const tr = (translate, key, fallback) => {
  const value = typeof translate === 'function' ? translate(key) : key;
  return value && value !== key ? value : fallback;
};

const normalizeSeries = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item?.day);
  if (value && typeof value === 'object') return Object.entries(value).map(([day, item]) => ({ day, value: item }));
  return [];
};

const collectEntities = (analyticData) => {
  const entities = [];
  const walk = (node, path = []) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    if (node.values_summary) {
      entities.push({
        id: path.join(':') || node.name || `entity-${entities.length + 1}`,
        name: node.name || path[path.length - 1] || `entity-${entities.length + 1}`,
        rawName: node.name,
        label: labelId(path[path.length - 1], "rac", node.name || path[path.length - 1]),
        areaLabel: labelId(path[path.length - 2], "ra", path.length > 1 ? path[path.length - 2].split("-").pop() : ""),
        area: path.length > 1 ? path[path.length - 2].split('-').pop() : '',
        node,
        values: normalizeSeries(node.values_summary),
      });
      return;
    }
    Object.entries(node).forEach(([key, value]) => walk(value, [...path, key]));
  };
  walk(analyticData || {});
  return entities;
};

const parseDay = (value) => {
  const parts = String(value || '').split(/[/-]/).map(Number);
  if (parts.length !== 3) return new Date(NaN);
  const [a, b, c] = parts;
  return a > 999 ? new Date(a, b - 1, c) : new Date(c, b - 1, a);
};

const sortPeriods = (values) => [...new Set(values.filter(Boolean))].sort((left, right) => {
  const a = parseDay(left).getTime();
  const b = parseDay(right).getTime();
  if (Number.isFinite(a) && Number.isFinite(b)) return a - b;
  const monthValue = (label) => {
    const match = String(label).trim().match(/^(.+)\s+(\d{4})$/);
    const month = match ? MONTHS.findIndex((name) => name.toLocaleLowerCase() === match[1].toLocaleLowerCase()) : -1;
    return month < 0 ? null : Number(match[2]) * 12 + month;
  };
  const monthA = monthValue(left);
  const monthB = monthValue(right);
  return monthA != null && monthB != null
    ? monthA - monthB
    : String(left).localeCompare(String(right), undefined, { numeric: true });
});

const metricValue = (value, key) => {
  const number = Number(value?.snapshot?.[key] ?? value?.[key]);
  return Number.isFinite(number) ? number : null;
};

const entityHeader = (level, translate) => tr(
  translate,
  level === 'centro' ? 'cage_label' : 'center_label',
  level === 'centro' ? 'Jaula' : 'Centro',
);

const baseColumns = (level, translate, includeArea = false) => [
  ...(includeArea ? [{ key: 'area', header: tr(translate, 'area', 'Área'), width: 20 }] : []),
  { key: 'entity', header: entityHeader(level, translate), width: 22 },
];

const totalFor = (entity, key, aggregation) => {
  const values = entity.values.map((entry) => metricValue(entry.value, key)).filter((value) => value != null);
  if (!values.length) return null;
  return aggregation === 'average'
    ? values.reduce((sum, value) => sum + value, 0) / values.length
    : values.reduce((sum, value) => sum + value, 0);
};

const alarmBand = (value, key) => {
  const band = value?.[key];
  const parsed = Number(band?.value ?? band?.alarm ?? band);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return Math.ceil((((d - new Date(Date.UTC(d.getUTCFullYear(), 0, 1))) / 86400000) + 1) / 7);
};

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const periodBucket = (day, groupMode) => {
  if (groupMode === 'day') return day;
  const date = parseDay(day);
  if (Number.isNaN(date.getTime())) return day;
  return groupMode === 'week' ? `Semana ${isoWeek(date)}` : `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const reportInfoSheet = ({
  translate,
  metadata,
  content,
  dailyDetailRange,
  dailyDetailCages,
  compact,
  level,
  groupMode,
  warnings = [],
}) => ({
  name: tr(translate, 'export_sheet_information', 'Información del reporte'),
  columns: [
    { key: 'field', header: tr(translate, 'export_column_field', 'Campo'), width: 28 },
    { key: 'value', header: tr(translate, 'value', 'Valor'), width: 60 },
  ],
  rows: [
    { field: tr(translate, 'export_column_menu', 'Menú'), value: tr(translate, 'analytic_menu', 'Analítica') },
    { field: tr(translate, 'region', 'Región'), value: labelRegion(metadata.region, metadata.region) },
    { field: tr(translate, 'area', 'Área'), value: labelArea({ region: metadata.region, area: metadata.area }, metadata.area) },
    { field: tr(translate, 'center_label', 'Centro'), value: labelCentro({ region: metadata.region, area: metadata.area, centro: metadata.center }, metadata.center) },
    { field: tr(translate, 'export_column_level', 'Nivel'), value: level },
    { field: tr(translate, 'temporal_range', 'Rango temporal'), value: `${metadata.dias?.startDate || ''} - ${metadata.dias?.endDate || ''}` },
    { field: tr(translate, 'export_column_granularity', 'Granularidad'), value: tr(translate, `fs_gran_${groupMode}`, groupMode) },
    { field: tr(translate, 'export_column_compact', 'Modo compactado'), value: compact },
    { field: tr(translate, 'export_content_daily_detail', 'Detalle diario por jaula'), value: content.dailyDetail && level === 'centro' },
    { field: tr(translate, 'export_range_title', 'Rango temporal del detalle diario'), value: tr(translate, `export_range_${dailyDetailRange}`, dailyDetailRange) },
    { field: tr(translate, 'export_cages_title', 'Jaulas del detalle diario'), value: tr(translate, `export_cages_${dailyDetailCages}`, dailyDetailCages) },
    { field: tr(translate, 'export_column_user', 'Usuario'), value: typeof localStorage === 'undefined' ? '' : localStorage.getItem('username') || '' },
    { field: tr(translate, 'export_column_generated', 'Generado'), value: new Date() },
    { field: tr(translate, 'export_column_version', 'Versión del exportador'), value: 'scapp-xlsx-2' },
    ...warnings.map((warning) => ({ field: tr(translate, 'export_column_warning', 'Advertencia'), value: warning })),
  ],
});

const timestampOf = (day, raw) => {
  if (raw instanceof Date) return new Date(raw.getTime());
  const direct = parseScappLocalDate(raw);
  if (!Number.isNaN(direct.getTime())) return direct;
  const date = parseDay(day);
  const time = String(raw || '').match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (Number.isNaN(date.getTime()) || !time) return null;
  date.setHours(Number(time[1]), Number(time[2]), Number(time[3] || 0), 0);
  return date;
};

const localIso = (date, fallback) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return fallback;
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const activitySources = (entity, compact) => {
  if (!compact) return [{ name: entity.name, data: normalizeSeries(entity.node.graph_activity) }];
  return Object.entries(entity.node)
    .filter(([key, value]) => key.startsWith('graph_activity') && value?.data)
    .map(([, value]) => ({ name: value.name || entity.name, data: normalizeSeries(value.data) }));
};

const addSeries = (rows, series, field, sourceName = '') => {
  normalizeSeries(series).forEach(({ day, value }) => {
    (Array.isArray(value) ? value : []).forEach((point) => {
      const date = timestampOf(day, point.time);
      const identity = `${date?.getTime() ?? `${day}-${point.time}`}|${sourceName}`;
      const row = rows.get(identity) || {
        day: Number.isNaN(parseDay(day).getTime()) ? day : { v: parseDay(day), s: 5 },
        time: date ? { v: date, s: 6 } : point.time,
        timestamp: localIso(date, `${day} ${point.time || ''}`.trim()),
        source: sourceName,
      };
      row[field] = metricValue(point, 'value');
      ['so2', 'o2', 'temp', 'cd', 'cs', 'depth', 'horizontal_position'].forEach((key) => {
        const valueNumber = metricValue(point, key);
        if (valueNumber != null) row[key] = valueNumber;
      });
      rows.set(identity, row);
    });
  });
};

const dailyEntities = (entities, dailyDetailCages, selectedEntity) => entities
  .filter((entity) => (
    dailyDetailCages !== 'selected'
    || entity.name === selectedEntity
    || entity.rawName === selectedEntity
    || entity.id === selectedEntity
  ))
  .sort((left, right) => String(left.rawName || left.name || left.id).localeCompare(
    String(right.rawName || right.name || right.id),
    undefined,
    { numeric: true, sensitivity: 'base' },
  ));

const dailyRows = (entity, compact, dailyDetailRange, selectedDay) => {
  if (dailyDetailRange === 'day' && !selectedDay) return [];
  const rows = new Map();
  const filterDay = (series) => (
    dailyDetailRange === 'day'
      ? normalizeSeries(series).filter((entry) => entry.day === selectedDay)
      : normalizeSeries(series)
  );
  Object.entries(FEEDING_RATES).forEach(([key, rate]) => {
    const column = key === 'tasa' ? 'feeding' : key === 'tasa_pfm' ? 'feedingPfm' : key;
    addSeries(rows, filterDay(entity.node[rate.graph]), column);
  });
  addSeries(rows, filterDay(entity.node.graph_perdidos), 'lost');
  activitySources(entity, compact).forEach((source) => (
    addSeries(rows, filterDay(source.data), 'activity', compact ? source.name : '')
  ));
  return Array.from(rows.values()).sort((left, right) => (
    String(left.timestamp).localeCompare(String(right.timestamp))
    || String(left.source).localeCompare(String(right.source))
  ));
};

const dailyDetailSheets = ({
  entities,
  translate,
  compact,
  dailyDetailRange,
  dailyDetailCages,
  selectedDay,
  selectedEntity,
}) => {
  const selected = dailyEntities(entities, dailyDetailCages, selectedEntity);
  const sheets = [];
  const indexRows = [];

  selected.forEach((entity, entityIndex) => {
    const outputRows = dailyRows(entity, compact, dailyDetailRange, selectedDay);
    if (!outputRows.length) return;
    const fallback = `${tr(translate, 'cage_label', 'Jaula')} (${entityIndex + 1})`;
    const rawName = String(entity.rawName || '').trim();
    const sheetName = rawName
      ? (compact ? `${tr(translate, 'cage_label', 'Jaula')} ${rawName}` : rawName)
      : fallback;
    const columns = [
      { key: 'day', header: tr(translate, 'day', 'Día'), width: 13 },
      { key: 'time', header: tr(translate, 'hour', 'Hora'), width: 20 },
      { key: 'timestamp', header: tr(translate, 'export_column_timestamp', 'Marca temporal'), width: 26 },
      ...(compact ? [{ key: 'source', header: tr(translate, 'export_column_source_cage', 'Jaula origen'), width: 20 }] : []),
      ...Object.entries(FEEDING_RATES).map(([key, rate]) => ({
        key: key === 'tasa' ? 'feeding' : key === 'tasa_pfm' ? 'feedingPfm' : key,
        header: `${tr(translate, 'rate', 'Tasa')} (${rate.unit})`, width: 20, style: 2,
      })),
      { key: 'activity', header: tr(translate, 'activity', 'Actividad'), width: 14, style: 2 },
      { key: 'lost', header: tr(translate, 'pellets_lost', 'Pellets perdidos'), width: 18, style: 2 },
      { key: 'so2', header: 'Oxígeno (SO2 %)', width: 18, style: 2 },
      { key: 'o2', header: 'Oxígeno (O2 mg/L)', width: 20, style: 2 },
      { key: 'temp', header: 'Temperatura (C)', width: 18, style: 2 },
      { key: 'cd', header: 'Corriente dirección (grados)', width: 27, style: 2 },
      { key: 'cs', header: 'Corriente velocidad (cm/s)', width: 28, style: 2 },
      { key: 'depth', header: 'Profundidad (m)', width: 18, style: 2 },
      { key: 'horizontal_position', header: `${tr(translate, 'horizontal_position', 'Posición horizontal')} (m)`, width: 24, style: 2 },
    ];
    sheets.push({ name: sheetName, columns, rows: outputRows, options: { freeze: 'C2' } });
    indexRows.push({
      entity: rawName || fallback,
      sheet: sheetName,
      included: (entity.node.note_camera_ids || []).join(', '),
      days: new Set(outputRows.map((row) => (
        row.day?.v instanceof Date ? row.day.v.toISOString().slice(0, 10) : String(row.day?.v ?? row.day)
      ))).size,
      rows: outputRows.length,
      first: outputRows[0]?.timestamp,
      last: outputRows[outputRows.length - 1]?.timestamp,
    });
  });

  if (!sheets.length) return [];
  return [{
    name: tr(translate, 'feeding_rate', 'Detalle diario'),
    columns: [
      { key: 'entity', header: tr(translate, 'cage_label', 'Jaula'), width: 20 },
      { key: 'sheet', header: tr(translate, 'export_column_sheet', 'Hoja'), width: 24 },
      { key: 'included', header: tr(translate, 'export_column_included_cages', 'Jaulas incluidas'), width: 28 },
      { key: 'days', header: tr(translate, 'export_column_days', 'Días'), width: 12, style: 3 },
      { key: 'rows', header: tr(translate, 'export_column_rows', 'Filas'), width: 12, style: 3 },
      { key: 'first', header: tr(translate, 'export_column_first_data', 'Primer dato'), width: 25 },
      { key: 'last', header: tr(translate, 'export_column_last_data', 'Último dato'), width: 25 },
    ],
    rows: indexRows,
  }, ...sheets];
};

export const estimateDailyDetailRows = (data, {
  dailyDetailRange = 'full',
  dailyDetailCages = 'all',
  selectedDay = '',
  selectedEntity = '',
  compact = false,
} = {}) => dailyEntities(
  collectEntities(data?.analytic_data),
  dailyDetailCages,
  selectedEntity,
).reduce(
  (total, entity) => total + dailyRows(entity, compact, dailyDetailRange, selectedDay).length,
  0,
);

export const getAnaliticaContentAvailability = (data, nivelSeleccionado = 'centro', compact = false) => {
  const entities = collectEntities(data?.analytic_data);
  return {
    summary: entities.length > 0,
    periodDetail: entities.some((entity) => entity.values.length),
    alarmRate: entities.some((entity) => Object.keys(FEEDING_RATES).some((key) => normalizeSeries(entity.node['alarms_by_' + key]).length) || normalizeSeries(entity.node.tasa_summary).length),
    ranking: entities.some((entity) => entity.values.some((entry) => (
      ['alarms_count', 'perdidos_count', 'visitas_count', 'sfr_value', 'delivered_food']
        .some((key) => metricValue(entry.value, key) != null)
    ))),
    alarmEvents: entities.some((entity) => normalizeSeries(entity.node.alarms_data)
      .some((entry) => Array.isArray(entry.value) && entry.value.length)),
    notes: Array.isArray(data?.notes) && data.notes.length > 0,
    dailyDetail: nivelSeleccionado === 'centro'
      && estimateDailyDetailRows(data, { compact }) > 0,
  };
};

export function buildAnaliticaSheets({
  data,
  nivelSeleccionado = 'centro',
  groupMode = 'day',
  compact = false,
  content = DEFAULT_ANALYTICS_CONTENT,
  dailyDetailRange = 'full',
  dailyDetailCages = 'all',
  selectedDay = '',
  selectedEntity = '',
  selectedRateKey = 'auto',
  translate,
  metadata = {},
  showAlarmReaction = false,
}) {
  const selectedContent = Object.fromEntries(Object.keys(DEFAULT_ANALYTICS_CONTENT).map((key) => [
    key,
    typeof content?.[key] === 'boolean' ? content[key] : DEFAULT_ANALYTICS_CONTENT[key],
  ]));
  const entities = collectEntities(data?.analytic_data);
  const includeArea = nivelSeleccionado === 'region';
  const periods = sortPeriods(entities.flatMap((entity) => entity.values.map((entry) => entry.day)));
  const sheets = [];
  const entityKind = nivelSeleccionado === 'centro' ? 'cage' : 'center';

  const summaryColumns = [
    ...baseColumns(nivelSeleccionado, translate, includeArea),
    ...METRICS.map(([key, labelKey, fallback]) => ({ key, header: tr(translate, labelKey, fallback), width: 20, style: 2 })),
  ];
  sheets.push({
    exportGroup: 'summary',
    name: tr(
      translate,
      `export_sheet_entity_summary_${entityKind}`,
      nivelSeleccionado === 'centro' ? 'Resumen por jaula' : 'Resumen por centro',
    ),
    columns: summaryColumns,
    rows: entities.map((entity) => ({
      area: entity.area, areaLabel: entity.areaLabel,
      entity: entity.name, entityLabel: entity.label,
      ...Object.fromEntries(METRICS.map(([key, , , aggregation]) => [key, totalFor(entity, key, aggregation)])),
    })),
  });

  const longRows = entities.flatMap((entity) => entity.values.map((entry) => ({
    area: entity.area, areaLabel: entity.areaLabel,
    entity: entity.name, entityLabel: entity.label,
    period: entry.day,
    ...Object.fromEntries(METRICS.map(([key]) => [key, metricValue(entry.value, key)])),
  })));
  sheets.push({
    exportGroup: 'periodDetail',
    name: tr(
      translate,
      `export_sheet_entity_period_${entityKind}`,
      nivelSeleccionado === 'centro' ? 'Jaula y periodo' : 'Centro y periodo',
    ),
    columns: [
      ...baseColumns(nivelSeleccionado, translate, includeArea),
      { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
      ...METRICS.map(([key, labelKey, fallback]) => ({ key, header: tr(translate, labelKey, fallback), width: 20, style: 2 })),
    ],
    rows: longRows,
  });

  METRICS.forEach(([key, labelKey, fallback, aggregation]) => {
    if (!longRows.some((row) => row[key] != null)) return;
    const columns = [
      ...baseColumns(nivelSeleccionado, translate, includeArea),
      ...periods.map((period, index) => ({ key: `p${index}`, header: period, width: 15, style: 2 })),
      { key: 'total', header: tr(translate, aggregation === 'average' ? 'average' : 'total', aggregation === 'average' ? 'Promedio' : 'Total'), width: 16, style: 2 },
    ];
    const entityRows = entities.map((entity) => {
      const byPeriod = new Map(entity.values.map((entry) => [entry.day, metricValue(entry.value, key)]));
      return {
        area: entity.area, areaLabel: entity.areaLabel,
        entity: entity.name, entityLabel: entity.label,
        ...Object.fromEntries(periods.map((period, index) => [`p${index}`, byPeriod.get(period)])),
        total: totalFor(entity, key, aggregation),
      };
    });
    const aggregate = (numbers) => {
      const values = numbers.filter((value) => value != null);
      if (!values.length) return null;
      return aggregation === 'average'
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : values.reduce((sum, value) => sum + value, 0);
    };
    sheets.push({
      exportGroup: 'summary',
      name: tr(translate, labelKey, fallback),
      columns,
      rows: [
        ...entityRows,
        {
          $style: 8,
          area: '',
          entity: tr(translate, 'total', 'Total'),
          ...Object.fromEntries(periods.map((_, index) => [`p${index}`, aggregate(entityRows.map((row) => row[`p${index}`]))])),
          total: aggregate(longRows.map((row) => row[key])),
        },
      ],
      options: { freeze: includeArea ? 'C2' : 'B2' },
    });
  });

  sheets.push({
    exportGroup: 'summary',
    name: tr(translate, 'export_sheet_period_totals', 'Totales por periodo'),
    columns: [
      { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
      ...METRICS.map(([key, labelKey, fallback]) => ({ key, header: tr(translate, labelKey, fallback), width: 20, style: 2 })),
    ],
    rows: periods.map((period) => {
      const values = entities.flatMap((entity) => entity.values.filter((entry) => entry.day === period).map((entry) => entry.value));
      return {
        period,
        ...Object.fromEntries(METRICS.map(([key, , , aggregation]) => {
          const numbers = values.map((value) => metricValue(value, key)).filter((value) => value != null);
          return [key, !numbers.length ? null : aggregation === 'average'
            ? numbers.reduce((sum, value) => sum + value, 0) / numbers.length
            : numbers.reduce((sum, value) => sum + value, 0)];
        })),
      };
    }),
  });

  const alarmRows = entities.flatMap((entity) => rateAlarmEntries({
    ...entity.node, alarms_by_tasa: normalizeSeries(entity.node.alarms_by_tasa || entity.node.tasa_summary),
  }, selectedRateKey)
    .map((entry) => ({
      area: entity.area, areaLabel: entity.areaLabel,
      entity: entity.name, entityLabel: entity.label,
      period: periodBucket(entry.day, groupMode),
      unit: entry.unit,
      low: alarmBand(entry.value, 'baja'),
      medium: alarmBand(entry.value, 'media'),
      high: alarmBand(entry.value, 'alta'),
    })));
  const alarmGrouped = Array.from(alarmRows.reduce((map, row) => {
    const key = `${row.area}|${row.entity}|${row.period}|${row.unit}`;
    const current = map.get(key) || { ...row, low: 0, medium: 0, high: 0 };
    current.low += row.low; current.medium += row.medium; current.high += row.high;
    map.set(key, current);
    return map;
  }, new Map()).values());
  if (alarmGrouped.length) sheets.push({
    exportGroup: 'alarmRate',
    name: tr(translate, 'alarm_rate', 'Alarmas por tasa'),
    columns: [
      ...baseColumns(nivelSeleccionado, translate, includeArea),
      { key: 'period', header: tr(translate, 'export_column_period', 'Periodo'), width: 18 },
      { key: 'unit', header: tr(translate, 'feeding_rate_title', 'Tasa de alimentación'), width: 20 },
      { key: 'low', header: tr(translate, 'low', 'Baja'), width: 14, style: 2 },
      { key: 'medium', header: tr(translate, 'medium', 'Media'), width: 14, style: 2 },
      { key: 'high', header: tr(translate, 'high', 'Alta'), width: 14, style: 2 },
    ],
    rows: alarmGrouped,
  });

  const rankRows = METRICS.filter(([key]) => ['alarms_count', 'perdidos_count', 'visitas_count', 'sfr_value', 'delivered_food'].includes(key))
    .flatMap(([key, labelKey, fallback, aggregation]) => [...entities]
      .map((entity) => ({ entity: entity.name, entityLabel: entity.label, value: totalFor(entity, key, aggregation) }))
      .filter((row) => row.value != null)
      .sort((a, b) => b.value - a.value)
      .map((row, index) => ({ metric: tr(translate, labelKey, fallback), position: index + 1, ...row })));
  if (rankRows.length) sheets.push({
    exportGroup: 'ranking',
    name: tr(translate, 'fs_ranking_title', 'Ranking'),
    columns: [
      { key: 'metric', header: tr(translate, 'export_column_metric', 'Métrica'), width: 24 },
      { key: 'position', header: tr(translate, 'export_column_position', 'Posición'), width: 12, style: 3 },
      { key: 'entity', header: tr(translate, 'export_column_entity', 'Entidad'), width: 22 },
      { key: 'value', header: tr(translate, 'value', 'Valor'), width: 18, style: 2 },
    ],
    rows: rankRows,
  });

  const notes = Array.isArray(data?.notes) ? data.notes : [];
  if (notes.length) sheets.push({
    exportGroup: 'notes',
    name: tr(translate, 'notes_chart_label', 'Notas'),
    columns: [
      { key: 'cages', header: tr(translate, 'cage_label', 'Jaula'), width: 24 },
      { key: 'date', header: tr(translate, 'export_column_datetime', 'Fecha y hora'), width: 22 },
      { key: 'author', header: tr(translate, 'export_column_author', 'Autor'), width: 20 },
      { key: 'note', header: tr(translate, 'notes_chart_label', 'Nota'), width: 60 },
    ],
    rows: notes.map((note) => ({
      cages: (note.cam_ids || []).join(', '),
      date: timestampOf('', note.timestamp) || note.timestamp,
      author: note.author || note.username || note.user || '',
      note: note.text || note.note || '',
    })),
  });

  const warnings = [];
  if (nivelSeleccionado !== 'centro') warnings.push(tr(translate, 'export_info_no_daily_detail', 'El detalle diario solo está disponible a nivel centro.'));
  if (compact) warnings.push(tr(translate, 'export_info_compact_warning', 'El modo compactado representa grupos de jaulas y conserva algunas métricas desde la primera jaula.'));
  if (selectedContent.dailyDetail && nivelSeleccionado === 'centro') {
    sheets.push(...dailyDetailSheets({
      entities,
      translate,
      compact,
      dailyDetailRange,
      dailyDetailCages,
      selectedDay,
      selectedEntity,
    }).map((sheet, index) => ({ ...sheet, exportGroup: index ? 'dailyCage' : 'dailyIndex' })));
  }

  const eventRows = entities.flatMap((entity) => normalizeSeries(entity.node.alarms_data).flatMap(({ day, value }) => (
    (Array.isArray(value) ? value : []).map((alarm) => ({
      entity: entity.name, entityLabel: entity.label,
      day,
      start: timestampOf(day, alarm.start),
      end: timestampOf(day, alarm.end),
      attended: alarm.atendido == null ? '' : alarm.atendido,
      latency: metricValue(alarm, 'latency_sec'),
    }))
  )));
  if (eventRows.length) sheets.push({
    exportGroup: 'alarmEvents',
    name: tr(translate, 'export_sheet_alarm_events', 'Alarmas (eventos)'),
    columns: [
      { key: 'entity', header: entityHeader(nivelSeleccionado, translate), width: 22 },
      { key: 'day', header: tr(translate, 'day', 'Día'), width: 14 },
      { key: 'start', header: tr(translate, 'export_column_start', 'Inicio'), width: 22 },
      { key: 'end', header: tr(translate, 'export_column_end', 'Fin'), width: 22 },
      ...(showAlarmReaction ? [
        { key: 'attended', header: tr(translate, 'export_column_attended', 'Atendida'), width: 14 },
        { key: 'latency', header: tr(translate, 'export_column_latency', 'Latencia (s)'), width: 16, style: 2 },
      ] : []),
    ],
    rows: eventRows,
  });

  sheets.push({
    ...reportInfoSheet({
      translate,
      metadata,
      content: selectedContent,
      dailyDetailRange,
      dailyDetailCages,
      compact,
      level: nivelSeleccionado,
      groupMode,
      warnings,
    }),
    exportGroup: 'information',
  });

  const enabledGroup = {
    summary: selectedContent.summary,
    periodDetail: selectedContent.periodDetail,
    alarmRate: selectedContent.alarmRate,
    ranking: selectedContent.ranking,
    alarmEvents: selectedContent.alarmEvents,
    notes: selectedContent.notes,
    dailyIndex: selectedContent.dailyDetail,
    dailyCage: selectedContent.dailyDetail,
    information: true,
  };
  const groupOrder = {
    information: 0,
    summary: 1,
    alarmRate: 2,
    ranking: 3,
    alarmEvents: 4,
    notes: 5,
    periodDetail: 6,
    dailyIndex: 7,
    dailyCage: 8,
  };
  return sheets
    .filter((sheet) => enabledGroup[sheet.exportGroup])
    .sort((left, right) => groupOrder[left.exportGroup] - groupOrder[right.exportGroup])
    // Presentation happens after grouping: equal labels never merge different entities.
    .map(({ exportGroup: _exportGroup, ...sheet }) => ({ ...sheet,
      rows: sheet.rows.map(({ entityLabel, areaLabel, ...row }) => ({ ...row,
        ...(entityLabel !== undefined ? { entity: entityLabel } : {}),
        ...(areaLabel !== undefined ? { area: areaLabel } : {}),
      })),
    }));
}
